import { registerCardFlowAction } from "@core/utils/cardFlow";
import type { RouteLocationRaw } from "vue-router";

import { setPendingSubscriptionPurchaseAction } from "@/utils/subscriptionPurchase";

export function translateLegacyHash(hash: string): RouteLocationRaw | undefined {
  if (!hash) {
    return undefined;
  }

  const withoutMarker = hash.startsWith("#") ? hash.slice(1) : hash;
  const normalizedHash = withoutMarker.replace(/^!/, "").replace(/^&/, "");
  if (!normalizedHash) {
    return undefined;
  }

  const [sectionCandidate, ...queryParts] = normalizedHash.split("?");
  const [sectionToken, ...ampersandTokens] = sectionCandidate.split("&");
  const querySegments: string[] = [];

  if (ampersandTokens.length > 0) {
    querySegments.push(ampersandTokens.join("&"));
  }

  if (queryParts.length > 0) {
    querySegments.push(queryParts.join("?"));
  }

  if (sectionToken.includes("=")) {
    querySegments.push(sectionToken);
  }

  const queryString = querySegments.join("&");
  const params = new URLSearchParams(queryString);

  if (params.has("accountRenderSubscriptionId")) {
    const subscriptionId = params.get("accountRenderSubscriptionId")?.trim();
    if (subscriptionId && subscriptionId.length > 0) {
      return { name: "subscription-details", params: { id: subscriptionId } };
    }
    return { name: "subscriptions" };
  }

  if (params.has("accountShowSubscriptionId")) {
    const subscriptionId = params.get("accountShowSubscriptionId")?.trim();
    if (subscriptionId && subscriptionId.length > 0) {
      return { name: "subscription-details", params: { id: subscriptionId } };
    }
    return { name: "subscriptions" };
  }

  if (params.has("accountRenderBuySubscriptionTemplateId")) {
    const templateId = params.get("accountRenderBuySubscriptionTemplateId")?.trim();
    if (templateId && templateId.length > 0) {
      setPendingSubscriptionPurchaseAction(params.get("accountNsAction"));
      return { name: "subscription-purchase", params: { templateId } };
    }
    return { name: "subscriptions" };
  }

  if (params.has("accountRenderSubscriptionBookingBySubscriptionId")) {
    const subscriptionId = params.get("accountRenderSubscriptionBookingBySubscriptionId")?.trim();
    if (subscriptionId && subscriptionId.length > 0) {
      return { name: "bookings", query: { subscriptionId } };
    }
    return { name: "bookings" };
  }

  if (params.has("myaccount")) {
    const action = params.get("myaccount");
    if (action === "delete") {
      return { name: "profile", replace: true };
    }
    if (action === "createGdprRequest") {
      return { name: "gdpr", replace: true };
    }
  }

  if (params.has("accountNsAction")) {
    const actionValue = params.get("accountNsAction");
    const context = registerCardFlowAction(actionValue);
    if (context?.originRoute) {
      return {
        name: context.originRoute,
        params: context.routeParams ?? {},
        query: context.routeQuery ?? {}
      };
    }
    return { name: "payment-sources" };
  }

  if (params.has("accountRenderMessage")) {
    return { name: "payment-sources" };
  }

  const sectionKey = sectionToken.split("=")[0]?.toLowerCase() ?? "";

  const sectionMap: Record<string, string> = {
    subscriptions: "subscriptions",
    "payment-sources": "payment-sources",
    vehicles: "vehicles",
    coupons: "coupons",
    receipts: "receipts",
    bookings: "bookings",
    profile: "profile",
    mysubscriptions: "subscriptions",
    "my-subscriptions": "subscriptions",
    mypaymentsources: "payment-sources",
    "payment-methods": "payment-sources",
    "payment-agreements": "vehicles",
    "my-payment-sources": "payment-sources",
    myvehicles: "vehicles",
    "my-vehicles": "vehicles",
    mysharedsubscriptions: "subscriptions",
    "my-shared-subscriptions": "subscriptions",
    mycoupons: "coupons",
    "my-coupons": "coupons",
    mytransactions: "receipts",
    "my-transactions": "receipts",
    mybookingreservations: "bookings",
    reservations: "bookings",
    "my-booking-reservations": "bookings",
    myaccount: "profile",
    "my-account": "profile",
    youpark: "youpark",
    youparktodos: "youpark-todos",
    youparkinsights: "youpark-insights",
    youparkaccess: "youpark-access",
    youparkcustomerregister: "youpark-customer-register",
    youparkcustomerregistersubscriptionedit: "youpark-customer-register-subscription-edit",
    youparkcustomerregistersubscriptionvehicles: "youpark-customer-register-subscription-vehicles",
    youparksubscriptions: "youpark-subscriptions",
    youparkinvoices: "youpark-invoices",
    youparktransactions: "youpark-transactions",
    youparkfreevisits: "youpark-free-visits",
    youparkfreevisitsnew: "youpark-free-visits-new",
    youparkfreevisitimport: "youpark-free-visits-import",
    youparkfreevisitedit: "youpark-free-visit-edit",
    youparkreports: "youpark-reports",
    youparkbusinessinsights: "youpark-business-insights",
    youparkcustomeradmin: "youpark-customer-admin",
    youparkcustomeradminlocations: "youpark-customer-admin-locations",
    youparkcustomeradminpaymentkiosks: "youpark-customer-admin-payment-kiosks",
    youparkcustomeradmininvoicing: "youpark-customer-admin-invoicing",
    youparkcustomeradminareas: "youpark-customer-admin-areas",
    youparkcustomeradminaccess: "youpark-customer-admin-access",
    youparkcustomeradminfreevisitkiosks: "youpark-customer-admin-free-visit-kiosks",
    youparkcustomeradminguestkiosks: "youpark-customer-admin-free-visit-kiosks",
    youparkcustomeradminfees: "youpark-customer-admin-fees",
    remit: "remit",
    remitallocations: "remit-allocations",
    remitcreditors: "remit-creditors"
  };

  const routeName = sectionMap[sectionKey];
  if (!routeName) {
    return undefined;
  }

  return { name: routeName };
}
